import { useState } from 'react';

import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { useTranslation } from '@/i18n/useTranslation';
import { useEnrolmentSession } from '@/lib/enrolmentSession';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { TapIcon } from './TapIcon';

/**
 * The front door [Figma · 01 · Welcome (tap to start), `12311:9092` · `9124` · `9155`].
 *
 * **One door since 2026-09-02.** The design split this screen: the new-or-returning question moved to
 * `02 · Have you applied?` and this became a single "Tap to start". KID-1's two doors are not gone — they
 * are one screen later, on `HaveYouAppliedScreen`.
 *
 * Rebuilt against the frame. The screen is the same card as every other, with **no header strip** — there is
 * nothing to go back to — a narrower 384px content column than the form screens, and centred type at a
 * larger scale because this is read from further away than a form is filled in.
 *
 * The type scale is **30/38 heading (-0.6 tracking) and 18/29 body**, read from the live frames on
 * 2026-09-01 and identical at all three widths (`12311:9092` desktop · `12311:9124` iPad · `12311:9155`
 * mobile). It was built as 30/34 and 18/28 — a plausible pair, and wrong by 4px on the title and 1px a line
 * on the subtitle. Those 6px are not cosmetic: the content block is `HUG`, so the whole footer sat 6px above
 * where the frame draws it and every box below the heading was reported as drifting. Together they make the
 * `Content` block exactly the drawn 160 (24 + 38 + 8 + 58 + 32).
 *
 * The door only sets copy. `openCheckIn` consumes it and stores a translation key; the door value itself
 * never reaches state, so nothing downstream can diagnose from it [decision 0001].
 */
export function WelcomeScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const checkVenueServes = useEnrolmentSession((state) => state.checkVenueServes);
  /*
   * The venue check is open. ⛔ **Not a screen** — no frame draws a waiting welcome and nothing new is drawn
   * [03-us-kad-6o] — it greys the door for the moment the probe is in flight, so a second tap is not a
   * second request, the same treatment the naming step's `namingInFlight` gives its action.
   */
  const [venueCheckOpen, setVenueCheckOpen] = useState(false);

  /*
   * ⭐ **The first tap asks the server whether this venue still serves, before the journey starts**
   * [03-us-kad-6o AC-2; decision 0430 § 1]. `published: true` starts it; a 401 or `published: false` has
   * already put the tablet on the plain pad by the time this resolves; ⛔ an unreadable answer claims no
   * outcome — the welcome stays and the next tap asks again, once [KAD-6i AC-10's posture].
   */
  const start = async (): Promise<void> => {
    setVenueCheckOpen(true);
    const serves = await checkVenueServes('tap');
    setVenueCheckOpen(false);
    if (serves) goTo(SCREEN.haveYouApplied);
  };

  /*
   * No Exit, because this screen IS the cleared state — the frame sets its `Exit button` instance
   * `visible: false` [`12438:26978`], and a control that ends a session nobody has started would offer the
   * candidate nothing.
   */
  return (
    <KioskScreen showExit={false}>
      <KioskCard
        contentWidth={384}
        footer={
          /*
           * ONE door, hug width and centred, which is what the frame draws since the design split this
           * screen on 2026-09-02. The two doors moved to `02 · Have you applied?` and live there now.
           *
           * It carries the frame's hand icon [`Icon / Hub / tap`] — drawn at all three widths, and absent
           * from the build until 2026-09-14.
           */
          <div className="flex w-full items-center justify-center">
            <Button
              disabled={venueCheckOpen}
              onClick={() => {
                void start();
              }}
            >
              {/*
                The hand, before the label and 8px from it — `Button` already carries the frame's `gap-2`,
                so the drawn 171×52 falls out of the label plus the icon plus the button's own padding.
              */}
              <TapIcon />
              {t('welcome.tapToStart')}
            </Button>
          </div>
        }
      >
        {/*
          `px-0 sm:px-6` rather than a flat `px-6`, because `KioskCard`'s content wrapper is `px-6 sm:px-0`:
          the two own the 24px inset at opposite widths and a flat value here ADDS to the card's below `sm`.
          That put the heading in a 279px column instead of 327 on `01 · Welcome — Mobile` (12021:40340),
          which is narrow enough to wrap the title onto a second line and push the buttons 46px down.
        */}
        <div className="flex w-full flex-col items-center px-0 pb-8 pt-6 text-center text-black sm:px-6">
          {/*
            The title and the subtitle are ONE block, 8px apart, and the block is what the frame draws:
            `Content` (384x160) holds a single child `Heading` (336x104 = 38 + 8 + 58) at all three widths
            [12311:9092 · 12311:9124 · 12311:9155, read 2026-09-02]. Built flat, the two texts were correct
            to the pixel and the group had no counterpart at all, so the check paired the frame's 104-tall
            group against our 38-tall `h1` and reported a 66px difference that was never on the screen —
            which is exactly what the `Heading` departure was suppressing.
          */}
          <div className="flex w-full flex-col items-center gap-2">
            <h1
              className="kiosk-heading-focus w-full text-[30px] font-bold leading-[38px] tracking-[-0.6px]"
              ref={heading}
              tabIndex={-1}
            >
              {t('welcome.title')}
            </h1>
            {/*
              `--muted-foreground`, not the block's `text-black`: the frame draws the subtitle grey (#6b7280)
              against a #111827 title, and every sibling screen — check-in, arrival, finish, submitted —
              already pairs a black heading with a muted subtitle. The registry's grey is #585756 rather than
              the frame's raw hex, so the token stays the source for colour and the frame for everything else.
            */}
            <p className="w-full text-lg leading-[29px] text-[var(--muted-foreground)]">
              {t('welcome.subtitle')}
            </p>
          </div>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
